import { __awaiter, __decorate } from "tslib";
import { makeObservable, observable, runInAction } from "mobx";
import { onSnapshot } from "mobx-state-tree";
import { LearningLogDocument, PersonalDocument } from "../../models/document/document-types";
import { GroupActivityBroadcaster } from "../group-activity-broadcaster";
import { DBLatestGroupIdListener } from "./db-latest-group-id-listener";
import { DBGroupsListener } from "./db-groups-listener";
import { DBGroupActivityListener } from "./db-group-activity-listener";
import { DBOtherDocumentsListener } from "./db-other-docs-listener";
import { DBProblemDocumentsListener } from "./db-problem-documents-listener";
import { DBPublicationsListener } from "./db-publications-listener";
import { DBSupportsListener } from "./db-supports-listener";
import { DBCommentsListener } from "./db-comments-listener";
import { DBBookmarksListener } from "./db-bookmarks-listener";
import { BaseListener } from "./base-listener";
import { DBDocumentsContentListener } from "./db-docs-content-listener";
import { DBStudentPersonalDocsListener } from "./db-student-personal-docs-listener";
import { DBExemplarsListener } from "./db-exemplars-listener";
export class DBListeners extends BaseListener {
    constructor(db) {
        super("DBListeners");
        Object.defineProperty(this, "isListening", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "latestGroupIdListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "groupsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "groupActivityListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "problemDocumentsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "personalDocumentsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "learningLogsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "publicationListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "studentPersonalDocsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "supportsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commentsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "bookmarksListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "documentsContentListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "exemplarsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "groupActivityBroadcaster", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // sync local support document properties to firebase (teachers only)
        // TODO: move this to client-side hook as was done with other document monitoring
        Object.defineProperty(this, "syncSupportDocumentProperties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (document, dbType, path) => {
                const { user } = this.db.stores;
                const { key } = document;
                if (dbType === "firebase") {
                    const updatePath = path || this.db.firebase.getUserDocumentMetadataPath(user, key, document.uid);
                    const updateRef = this.db.firebase.ref(updatePath);
                    // synchronize document property changes to firebase
                    onSnapshot(document.properties, properties => updateRef.update({ properties }));
                }
                else if (dbType === "firestore") {
                    const docRef = this.db.firestore.getMulticlassSupportDocumentRef(key);
                    // synchronize document property changes to firestore
                    onSnapshot(document.properties, properties => docRef.update({ properties }));
                }
            }
        });
        makeObservable(this);
        this.db = db;
        this.latestGroupIdListener = new DBLatestGroupIdListener(db);
        this.groupsListener = new DBGroupsListener(db);
        this.groupActivityListener = new DBGroupActivityListener(db);
        this.problemDocumentsListener = new DBProblemDocumentsListener(db);
        this.personalDocumentsListener = new DBOtherDocumentsListener(db, PersonalDocument);
        this.learningLogsListener = new DBOtherDocumentsListener(db, LearningLogDocument);
        this.publicationListener = new DBPublicationsListener(db);
        this.studentPersonalDocsListener = new DBStudentPersonalDocsListener(db, PersonalDocument);
        this.supportsListener = new DBSupportsListener(db);
        this.commentsListener = new DBCommentsListener(db);
        this.bookmarksListener = new DBBookmarksListener(db);
        this.documentsContentListener = new DBDocumentsContentListener(db);
        this.exemplarsListener = new DBExemplarsListener(db);
        this.groupActivityBroadcaster = new GroupActivityBroadcaster(db);
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            // listeners must start in this order so we know the latest group joined so we can autojoin groups if needed
            yield this.latestGroupIdListener.start();
            // start group and document listeners
            yield Promise.all([
                this.groupsListener.start(),
                this.problemDocumentsListener.start(),
                this.personalDocumentsListener.start(),
                this.learningLogsListener.start(),
                this.publicationListener.start(),
                this.studentPersonalDocsListener.start(),
                this.supportsListener.start()
            ]);
            // start group activity listener after groups listener so currentGroupId is set
            yield this.groupActivityListener.start();
            this.groupActivityBroadcaster.start();
            // start listeners that depend on documents
            yield Promise.all([
                this.commentsListener.start(),
                this.bookmarksListener.start(),
                this.documentsContentListener.start(),
                this.exemplarsListener.start()
            ]);
            runInAction(() => this.isListening = true);
        });
    }
    stop() {
        runInAction(() => this.isListening = false);
        this.documentsContentListener.stop();
        this.bookmarksListener.stop();
        this.commentsListener.stop();
        this.supportsListener.stop();
        this.publicationListener.stop();
        this.studentPersonalDocsListener.stop();
        this.learningLogsListener.stop();
        this.personalDocumentsListener.stop();
        this.problemDocumentsListener.stop();
        this.groupActivityBroadcaster.stop();
        this.groupActivityListener.stop();
        this.groupsListener.stop();
        this.latestGroupIdListener.stop();
        this.exemplarsListener.stop();
    }
}
__decorate([
    observable
], DBListeners.prototype, "isListening", void 0);
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaW5kZXguanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbGliL2RiLWxpc3RlbmVycy9pbmRleC50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiO0FBQUEsT0FBTyxFQUFFLGNBQWMsRUFBRSxVQUFVLEVBQUUsV0FBVyxFQUFFLE1BQU0sTUFBTSxDQUFDO0FBQy9ELE9BQU8sRUFBRSxVQUFVLEVBQUUsTUFBTSxpQkFBaUIsQ0FBQztBQUc3QyxPQUFPLEVBQUUsbUJBQW1CLEVBQUUsZ0JBQWdCLEVBQUUsTUFBTSxzQ0FBc0MsQ0FBQztBQUc3RixPQUFPLEVBQUUsd0JBQXdCLEVBQUUsTUFBTSwrQkFBK0IsQ0FBQztBQUN6RSxPQUFPLEVBQUUsdUJBQXVCLEVBQUUsTUFBTSwrQkFBK0IsQ0FBQztBQUN4RSxPQUFPLEVBQUUsZ0JBQWdCLEVBQUUsTUFBTSxzQkFBc0IsQ0FBQztBQUN4RCxPQUFPLEVBQUUsdUJBQXVCLEVBQUUsTUFBTSw4QkFBOEIsQ0FBQztBQUN2RSxPQUFPLEVBQUUsd0JBQXdCLEVBQUUsTUFBTSwwQkFBMEIsQ0FBQztBQUNwRSxPQUFPLEVBQUUsMEJBQTBCLEVBQUUsTUFBTSxpQ0FBaUMsQ0FBQztBQUM3RSxPQUFPLEVBQUUsc0JBQXNCLEVBQUUsTUFBTSw0QkFBNEIsQ0FBQztBQUNwRSxPQUFPLEVBQUUsa0JBQWtCLEVBQUUsTUFBTSx3QkFBd0IsQ0FBQztBQUM1RCxPQUFPLEVBQUUsa0JBQWtCLEVBQUUsTUFBTSx3QkFBd0IsQ0FBQztBQUM1RCxPQUFPLEVBQUUsbUJBQW1CLEVBQUUsTUFBTSx5QkFBeUIsQ0FBQztBQUM5RCxPQUFPLEVBQUUsWUFBWSxFQUFFLE1BQU0saUJBQWlCLENBQUM7QUFDL0MsT0FBTyxFQUFFLDBCQUEwQixFQUFFLE1BQU0sNEJBQTRCLENBQUM7QUFDeEUsT0FBTyxFQUFFLDZCQUE2QixFQUFFLE1BQU0scUNBQXFDLENBQUM7QUFDcEYsT0FBTyxFQUFFLG1CQUFtQixFQUFFLE1BQU0seUJBQXlCLENBQUM7QUFFOUQsTUFBTSxPQUFPLFdBQVksU0FBUSxZQUFZO0lBbUIzQyxZQUFZLEVBQU07UUFDaEIsS0FBSyxDQUFDLGFBQWEsQ0FBQyxDQUFDO1FBbkJKOzs7O21CQUFjLEtBQUs7V0FBQztRQUMvQjs7Ozs7V0FBTztRQUVQOzs7OztXQUErQztRQUMvQzs7Ozs7V0FBaUM7UUFDakM7Ozs7O1dBQStDO1FBQy9DOzs7OztXQUFxRDtRQUNyRDs7Ozs7V0FBb0Q7UUFDcEQ7Ozs7O1dBQStDO1FBQy9DOzs7OztXQUE0QztRQUM1Qzs7Ozs7V0FBMkQ7UUFDM0Q7Ozs7O1dBQXFDO1FBQ3JDOzs7OztXQUFxQztRQUNyQzs7Ozs7V0FBdUM7UUFDdkM7Ozs7O1dBQXFEO1FBQ3JEOzs7OztXQUF1QztRQUN2Qzs7Ozs7V0FBbUQ7UUFvRTNELHFFQUFxRTtRQUNyRSxpRkFBaUY7UUFDMUU7Ozs7bUJBQWdDLENBQUMsUUFBMkIsRUFBRSxNQUFvQixFQUFFLElBQWEsRUFBRSxFQUFFO2dCQUMxRyxNQUFNLEVBQUUsSUFBSSxFQUFFLEdBQUcsSUFBSSxDQUFDLEVBQUUsQ0FBQyxNQUFNLENBQUM7Z0JBQ2hDLE1BQU0sRUFBRSxHQUFHLEVBQUUsR0FBRyxRQUFRLENBQUM7Z0JBRXpCLElBQUksTUFBTSxLQUFLLFVBQVUsRUFBRSxDQUFDO29CQUMxQixNQUFNLFVBQVUsR0FBRyxJQUFJLElBQUksSUFBSSxDQUFDLEVBQUUsQ0FBQyxRQUFRLENBQUMsMkJBQTJCLENBQUMsSUFBSSxFQUFFLEdBQUcsRUFBRSxRQUFRLENBQUMsR0FBRyxDQUFDLENBQUM7b0JBQ2pHLE1BQU0sU0FBUyxHQUFHLElBQUksQ0FBQyxFQUFFLENBQUMsUUFBUSxDQUFDLEdBQUcsQ0FBQyxVQUFVLENBQUMsQ0FBQztvQkFDbkQsb0RBQW9EO29CQUNwRCxVQUFVLENBQUMsUUFBUSxDQUFDLFVBQVUsRUFBRSxVQUFVLENBQUMsRUFBRSxDQUFDLFNBQVMsQ0FBQyxNQUFNLENBQUMsRUFBRSxVQUFVLEVBQUUsQ0FBQyxDQUFDLENBQUM7Z0JBQ2xGLENBQUM7cUJBQ0ksSUFBSSxNQUFNLEtBQUssV0FBVyxFQUFFLENBQUM7b0JBQ2hDLE1BQU0sTUFBTSxHQUFHLElBQUksQ0FBQyxFQUFFLENBQUMsU0FBUyxDQUFDLCtCQUErQixDQUFDLEdBQUcsQ0FBQyxDQUFDO29CQUN0RSxxREFBcUQ7b0JBQ3JELFVBQVUsQ0FBQyxRQUFRLENBQUMsVUFBVSxFQUFFLFVBQVUsQ0FBQyxFQUFFLENBQUMsTUFBTSxDQUFDLE1BQU0sQ0FBQyxFQUFFLFVBQVUsRUFBRSxDQUFDLENBQUMsQ0FBQztnQkFDL0UsQ0FBQztZQUNILENBQUM7V0FBQztRQWpGQSxjQUFjLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDckIsSUFBSSxDQUFDLEVBQUUsR0FBRyxFQUFFLENBQUM7UUFDYixJQUFJLENBQUMscUJBQXFCLEdBQUcsSUFBSSx1QkFBdUIsQ0FBQyxFQUFFLENBQUMsQ0FBQztRQUM3RCxJQUFJLENBQUMsY0FBYyxHQUFHLElBQUksZ0JBQWdCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDL0MsSUFBSSxDQUFDLHFCQUFxQixHQUFHLElBQUksdUJBQXVCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDN0QsSUFBSSxDQUFDLHdCQUF3QixHQUFHLElBQUksMEJBQTBCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDbkUsSUFBSSxDQUFDLHlCQUF5QixHQUFHLElBQUksd0JBQXdCLENBQUMsRUFBRSxFQUFFLGdCQUFnQixDQUFDLENBQUM7UUFDcEYsSUFBSSxDQUFDLG9CQUFvQixHQUFHLElBQUksd0JBQXdCLENBQUMsRUFBRSxFQUFFLG1CQUFtQixDQUFDLENBQUM7UUFDbEYsSUFBSSxDQUFDLG1CQUFtQixHQUFHLElBQUksc0JBQXNCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDMUQsSUFBSSxDQUFDLDJCQUEyQixHQUFHLElBQUksNkJBQTZCLENBQUMsRUFBRSxFQUFFLGdCQUFnQixDQUFDLENBQUM7UUFDM0YsSUFBSSxDQUFDLGdCQUFnQixHQUFHLElBQUksa0JBQWtCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDbkQsSUFBSSxDQUFDLGdCQUFnQixHQUFHLElBQUksa0JBQWtCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDbkQsSUFBSSxDQUFDLGlCQUFpQixHQUFHLElBQUksbUJBQW1CLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDckQsSUFBSSxDQUFDLHdCQUF3QixHQUFHLElBQUksMEJBQTBCLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDbkUsSUFBSSxDQUFDLGlCQUFpQixHQUFHLElBQUksbUJBQW1CLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDckQsSUFBSSxDQUFDLHdCQUF3QixHQUFHLElBQUksd0JBQXdCLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDbkUsQ0FBQztJQUVZLEtBQUs7O1lBQ2hCLDRHQUE0RztZQUM1RyxNQUFNLElBQUksQ0FBQyxxQkFBcUIsQ0FBQyxLQUFLLEVBQUUsQ0FBQztZQUN6QyxxQ0FBcUM7WUFDckMsTUFBTSxPQUFPLENBQUMsR0FBRyxDQUFDO2dCQUNoQixJQUFJLENBQUMsY0FBYyxDQUFDLEtBQUssRUFBRTtnQkFDM0IsSUFBSSxDQUFDLHdCQUF3QixDQUFDLEtBQUssRUFBRTtnQkFDckMsSUFBSSxDQUFDLHlCQUF5QixDQUFDLEtBQUssRUFBRTtnQkFDdEMsSUFBSSxDQUFDLG9CQUFvQixDQUFDLEtBQUssRUFBRTtnQkFDakMsSUFBSSxDQUFDLG1CQUFtQixDQUFDLEtBQUssRUFBRTtnQkFDaEMsSUFBSSxDQUFDLDJCQUEyQixDQUFDLEtBQUssRUFBRTtnQkFDeEMsSUFBSSxDQUFDLGdCQUFnQixDQUFDLEtBQUssRUFBRTthQUM5QixDQUFDLENBQUM7WUFDSCwrRUFBK0U7WUFDL0UsTUFBTSxJQUFJLENBQUMscUJBQXFCLENBQUMsS0FBSyxFQUFFLENBQUM7WUFDekMsSUFBSSxDQUFDLHdCQUF3QixDQUFDLEtBQUssRUFBRSxDQUFDO1lBQ3RDLDJDQUEyQztZQUMzQyxNQUFNLE9BQU8sQ0FBQyxHQUFHLENBQUM7Z0JBQ2hCLElBQUksQ0FBQyxnQkFBZ0IsQ0FBQyxLQUFLLEVBQUU7Z0JBQzdCLElBQUksQ0FBQyxpQkFBaUIsQ0FBQyxLQUFLLEVBQUU7Z0JBQzlCLElBQUksQ0FBQyx3QkFBd0IsQ0FBQyxLQUFLLEVBQUU7Z0JBQ3JDLElBQUksQ0FBQyxpQkFBaUIsQ0FBQyxLQUFLLEVBQUU7YUFDL0IsQ0FBQyxDQUFDO1lBRUgsV0FBVyxDQUFDLEdBQUcsRUFBRSxDQUFDLElBQUksQ0FBQyxXQUFXLEdBQUcsSUFBSSxDQUFDLENBQUM7UUFDN0MsQ0FBQztLQUFBO0lBRU0sSUFBSTtRQUNULFdBQVcsQ0FBQyxHQUFHLEVBQUUsQ0FBQyxJQUFJLENBQUMsV0FBVyxHQUFHLEtBQUssQ0FBQyxDQUFDO1FBRTVDLElBQUksQ0FBQyx3QkFBd0IsQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUNyQyxJQUFJLENBQUMsaUJBQWlCLENBQUMsSUFBSSxFQUFFLENBQUM7UUFDOUIsSUFBSSxDQUFDLGdCQUFnQixDQUFDLElBQUksRUFBRSxDQUFDO1FBQzdCLElBQUksQ0FBQyxnQkFBZ0IsQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUM3QixJQUFJLENBQUMsbUJBQW1CLENBQUMsSUFBSSxFQUFFLENBQUM7UUFDaEMsSUFBSSxDQUFDLDJCQUEyQixDQUFDLElBQUksRUFBRSxDQUFDO1FBQ3hDLElBQUksQ0FBQyxvQkFBb0IsQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUNqQyxJQUFJLENBQUMseUJBQXlCLENBQUMsSUFBSSxFQUFFLENBQUM7UUFDdEMsSUFBSSxDQUFDLHdCQUF3QixDQUFDLElBQUksRUFBRSxDQUFDO1FBQ3JDLElBQUksQ0FBQyx3QkFBd0IsQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUNyQyxJQUFJLENBQUMscUJBQXFCLENBQUMsSUFBSSxFQUFFLENBQUM7UUFDbEMsSUFBSSxDQUFDLGNBQWMsQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUMzQixJQUFJLENBQUMscUJBQXFCLENBQUMsSUFBSSxFQUFFLENBQUM7UUFDbEMsSUFBSSxDQUFDLGlCQUFpQixDQUFDLElBQUksRUFBRSxDQUFDO0lBQ2hDLENBQUM7Q0FxQkY7QUF2R29CO0lBQWxCLFVBQVU7Z0RBQTRCIn0=